/**
 * 项目相关类型定义
 * 参考：api-design-specification.md
 */

import type { ContractorSettlementReportTerminateResponse } from './contractor-settlement-report';
import type { OrgMemberRole, RoleTemplateCode } from './org';

/**
 * 项目设置
 */
export interface ProjectSettings {
  resolution: {
    width: number;
    height: number;
  };
  frameRate: number;
  duration: number;
  exportFormat?: 'mp4' | 'mov' | 'avi';
  exportQuality?: 'low' | 'medium' | 'high';
  /** 父项目总授信预算；父项目 aiCreditsBudget 仅表示单集默认模板 */
  totalCreditsBudget?: number;
  creditAllocationPending?: boolean;
  enterpriseBaseSetupCompletedAt?: string | null;
  enterpriseSetup?: {
    status?: string | null;
    producerSetupCompletedAt?: string | null;
    baseSetupCompletedAt?: string | null;
    memberSetupCompletedAt?: string | null;
    memberSetupCompletedBy?: string | null;
    memberSetupCompletionMode?: 'configured' | 'skipped' | null;
    nextAction?: string | null;
    directorCanManageMembers?: boolean;
    directorCanCreateScreenplays?: boolean;
    note?: string | null;
    configuredSubprojectCount?: number;
    skippedSubprojectCount?: number;
    affectedProjectIds?: string[];
  };
  memberConfigInheritance?: MemberConfigInheritanceSummary | null;
}

/**
 * 项目内容类型（影视类型）
 */
export type ProjectContentType = 'ad' | 'movie' | 'series' | 'anime' | 'short' | 'concept';

/**
 * 画幅比例
 */
export type AspectRatioType = '16:9' | '9:16' | '4:3' | '21:9' | '1:1' | '2.35:1' | '2.39:1';

/**
 * 项目类型（所有权类型）
 * 后端返回字符串：'mine' | 'collab'
 */
export type ProjectType = 'mine' | 'collab';
export type ProjectMemberRole = 'owner' | 'editor' | 'viewer';

/**
 * 项目类型常量（避免硬编码）
 */
export const PROJECT_TYPE = {
  MINE: 'mine',
  COLLAB: 'collab',
} as const;

/**
 * 项目状态
 * 后端返回字符串：'active' | 'paused' | 'archived' | 'trashed' | 'soft_deleted'
 */
export type ProjectStatus = 'active' | 'paused' | 'archived' | 'trashed' | 'soft_deleted';

/**
 * 项目状态常量（避免硬编码）
 */
export const PROJECT_STATUS = {
  ACTIVE: 'active',
  PAUSED: 'paused',
  ARCHIVED: 'archived',
  TRASHED: 'trashed',
  SOFT_DELETED: 'soft_deleted',
} as const;

/**
 * 项目范围（RFC-004）：与 ProjectType 并列，但含义是"以我为视角的归属"
 *  - mine   : 我创建/我拥有
 *  - collab : 我被邀请协作（含跨组织 PCO）
 */
export type ProjectScope = 'mine' | 'collab';

export type FunctionalRoleCode =
  | 'director'
  | 'storyboard_artist'
  | 'visual_designer'
  | 'resource_producer'
  | 'voice_actor'
  | 'sound_designer'
  | 'post_production_artist'
  | 'observer';

export interface FunctionalRoleDefinition {
  code: FunctionalRoleCode;
  label: string;
  description: string;
}

/**
 * 跨组织协作方向（RFC-004）
 *  - outgoing : 本项目归属的组织是"我方"
 *  - incoming : 我方通过 PCO 被邀请加入
 *  - null/undefined : 非跨组织情形
 */
export type CollabDirection = 'outgoing' | 'incoming';

/**
 * 项目（API 数据结构）
 * 对应 GET /api/v1/projects 返回的数据
 */
export interface Project {
  id: string;
  name: string;
  description?: string;
  /** RFC-148 兼容字段：仍可能承载旧"当前用户有效类型"语义；协作展示请使用 viewScope */
  type: ProjectType;
  /** RFC-148：当前用户视角的项目归属。internal=内部成员，external=外部协作者 */
  viewScope?: 'internal' | 'external';
  /** RFC-148：项目所有者实体类型 user=个人项目 / org=组织项目 */
  ownerType?: 'user' | 'org';
  ownerId: string;
  /** 项目创建操作者用户 ID；由服务端登录态写入，创建请求不可传 */
  createdBy: string;
  /** 项目归属组织 ID（null 表示个人项目） */
  orgId?: string | null;
  /** 项目归属组织名称（列表视图冗余字段，RFC-004） */
  orgName?: string | null;
  /** 项目归属组织类型：1=企业, 2=团队 */
  orgType?: number | null;
  /** 立项时创建人的组织角色快照 */
  initiatedByRole?: OrgMemberRole | number | null;
  /** 立项时创建人的系统权限角色模板快照 */
  initiatedByRoleTemplateCode?: RoleTemplateCode | string | null;
  /** 当前视角下的跨组织协作方向（RFC-004） */
  collabDirection?: CollabDirection | null;
  /** 当前用户在该项目中的成员类型（S3 临时成员合并列表） */
  memberType?: 'regular' | 'temp' | null;
  /** 当前用户在该项目中的有效成员角色 */
  memberRole?: ProjectMemberRole | null;
  /** 临时成员身份到期时间 */
  expiresAt?: string | null;
  /** 当前用户在该项目中的项目职能；空数组表示未分配 */
  functionalRoles?: ProjectMemberFunctionalRole[];
  /** 当前用户是否可下载本项目受控素材；个人项目所有者始终为 true */
  canDownload?: boolean;
  /** 当前用户是否具备成员管理配置权 */
  canManageMembers?: boolean;
  /** 当前用户是否能授予或收回「允许管理本集成员」 */
  canGrantMemberManagement?: boolean;
  /** 当前用户是否能授予父级建集权 */
  canConfigureDirectorScreenplays?: boolean;
  /** 当前用户是否持有父级建集/上传剧本权 */
  canCreateScreenplays?: boolean;
  /** 当前用户项目编辑权限被限制时的业务原因 */
  permissionRestrictionReason?: string | null;
  /** 卡片点击时应进入的实际项目；父级项目可指向当前用户可访问的子项目 */
  entryProjectId?: string | null;
  /** 父项目下是否已有配置导演的子项目 */
  hasConfiguredSubproject?: boolean;
  hasConfiguredMembers?: boolean;
  subprojects?: Project[];
  folderId?: string; // 所属文件夹ID
  parentProjectId?: string; // 父项目ID（子项目架构）
  screenplayId?: string; // 关联剧本ID
  screenplayCount?: number; // 子项目剧本数量
  displayOrder: number; // 显示顺序
  thumbnailUrl?: string;
  coverImageId?: string; // 封面图片ID
  aiCreditsBudget?: number; // 父项目为单集默认预算；子项目为本集实际预算（算力）
  budgetConsumed?: number; // 已消耗算力示值（服务端将 token 累计按倍率折算）
  budgetRemaining?: number; // 剩余预算算力
  creditAllocationPending?: boolean; // 私人项目迁入组织后的待设置预算标记
  settings: ProjectSettings;
  status: ProjectStatus;
  workflowStage?: number | null;
  workflowStatus?: number | null;
  workflowStageUpdatedAt?: string | null;
  workflowStageUpdatedBy?: string | null;
  createdAt: string;
  updatedAt: string;
  archivedAt?: string | null;
  archivedBy?: string | null;
  archiveReason?: string | null;
  custodianUserId?: string | null;
  custodianAssignedAt?: string | null;
  custodianReason?: string | null;
  creditReleasedAt?: string | null;
  creditReleasedAmount?: number;
  creditOverdraftConsumed?: number;
  pausedAt?: string | null;
  pausedBy?: string | null;
  pauseReason?: string | null;
  workflowStageBeforePause?: number | null;
  trashedAt?: string; // 进入回收站时间
  deletedAt?: string; // 软删除时间
  /** HATEOAS 链接（仅在详情接口返回） */
  _links?: {
    storyboards: string;
    members: string;
    storyboardBoard: string;
  };
  // 影视项目元数据
  contentType?: ProjectContentType; // 项目内容类型（广告片/电影/剧集等）
  aspectRatio?: AspectRatioType; // 画幅比例
  plannedDuration?: number; // 计划总时长（秒）
  actualDuration?: number; // 实际总时长（秒）
  episodes?: number; // 集数
  styleId?: string; // 系统视觉风格ID（指向 visual_styles.styleId）
  visualStyle?: string; // 自定义视觉风格提示词
  tagline?: string;
  synopsis?: string;
  // 前端状态字段
  cloningStatus?: 'cloning' | 'success' | 'failed';
  cloningProgress?: number;
  // RFC-003：子项目/EPN 相关
  /**
   * 承制项目访问上下文（阶段 2）：当前 active_org 作为承制方时的 PCO 与预算能力。
   * 由项目详情 API 在 incoming/external 视角下返回；未实现时前端不依赖。
   */
  activePcoId?: string | null;
  activeContractType?: CollaboratorContractType | null;
  /** 全包承制：当前承制方预算是否已配置 */
  contractBudgetConfigured?: boolean;
  /** 全包承制：是否可配置承制预算 */
  canManageContractBudget?: boolean;
  /** 制作承制承制方：是否隐藏项目预算数字（走主办方预算） */
  hideProjectBudgetMetrics?: boolean;
  /** 是否存在子项目 —— true 表示聚合节点，对成员/预算/任务操作为只读 */
  hasSubprojects?: boolean;
  /** 成员作用域层级（1 = EPN，可被分配；2 = 聚合节点，只读） */
  memberScopeLevel?: 1 | 2;
  /** 父项目后续子项目成员默认来源摘要 */
  memberConfigInheritance?: MemberConfigInheritanceSummary | null;
}

/**
 * 创建项目时选中的共享资源项（ADR 02）
 * 与后端 SharedResourceItem 一致
 */
export interface SharedResourceItemDto {
  sourceType: 'folder' | 'project' | 'resource';
  sourceId: string;
  shareType: 1 | 2 | 3; // 1=整个项目, 2=特定资源类型, 3=特定资源
  resourceType?: number | null; // 1=角色, 2=场景, 3=道具, 4=素材（shareType=2 或 3 时必填）
}

/**
 * 创建项目 DTO
 */
export interface CreateProjectDto {
  name: string;
  description?: string;
  type?: ProjectType;
  folderId?: string;
  contentType?: ProjectContentType;
  aspectRatio?: AspectRatioType;
  plannedDuration?: number;
  episodes?: number;
  styleId?: string | null;
  visualStyle?: string | null;
  /** 企业非制片人创建根项目时必须指派的主负责制片人 */
  producerUserId?: string | null;
  settings?: Partial<ProjectSettings>;
  /** 新建项目时关联的共享资源（ADR 02） */
  sharedResources?: SharedResourceItemDto[];
  /** 团队/企业下创建根项目时必填：父项目初始总授信预算（算力）。子项目勿传 */
  initialCreditsBudget?: number;
  /** 团队/企业下创建根项目时的父项目单集预算模板（算力） */
  aiCreditsBudget?: number;
  /** 企业 OWNER/ADMIN 立项时是否以项目管控关系加入项目 */
  adminParticipatesInProject?: boolean;
}

/**
 * 更新项目 DTO
 */
export interface UpdateProjectDto {
  name?: string;
  description?: string | null;
  folderId?: string | null;
  thumbnailUrl?: string;
  contentType?: ProjectContentType;
  aspectRatio?: AspectRatioType;
  plannedDuration?: number;
  actualDuration?: number;
  episodes?: number;
  styleId?: string | null;
  visualStyle?: string | null;
  settings?: Partial<ProjectSettings>;
  /** 编辑时同步的共享资源（传空数组清空，不传则不修改） */
  sharedResources?: SharedResourceItemDto[];
  /** 组织根/父级项目：更新单集预算模板（算力）；不影响已创建子项目和父总授信 */
  aiCreditsBudget?: number;
}

export interface SetProjectCoverImageDto {
  attachmentId: string;
}

/**
 * 移动项目 DTO
 */
export interface MoveProjectDto {
  folderId?: string;
}

export interface ArchiveProjectDto {
  reason?: string;
}

export interface ArchiveProjectResponse {
  projectId: string;
  status: ProjectStatus;
  archivedAt?: string | null;
  archivedBy?: string | null;
  custodianUserId?: string | null;
  creditReleasedAmount: number;
  terminatedPcoReports?: ContractorSettlementReportTerminateResponse[];
}

export interface PauseProjectDto {
  reason?: string;
}

export interface PauseProjectResponse {
  projectId: string;
  status: ProjectStatus;
  pausedAt?: string | null;
  pausedBy?: string | null;
  pauseReason?: string | null;
  workflowStageBeforePause?: number | null;
}

/**
 * 项目列表查询参数
 */
export interface ProjectListParams {
  /** @deprecated 兼容旧入参：mine/collab 按服务端兼容语义过滤 */
  type?: ProjectType;
  /** @deprecated RFC-004 旧入参：mine/collab 按"视角归属"过滤，含跨组织 */
  scope?: ProjectScope;
  /** RFC-148 目标入参：internal=内部项目，external=协作项目；后端完全支持后替代 scope */
  viewScope?: 'internal' | 'external';
  folderId?: string;
  contentType?: ProjectContentType;
  search?: string;
  sortBy?: 'createdAt' | 'updatedAt' | 'name' | 'displayOrder';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

/**
 * RFC-004：跨组织项目协作（PCO）— 角色与状态
 */
export type CollabRole = 'editor' | 'viewer';
export type CollabStatus = 'pending' | 'active' | 'revoked' | 'rejected' | 'expired';
export type CollaboratorContractType = 1 | 2;

export const COLLABORATOR_CONTRACT_TYPE = {
  PRODUCTION: 1,
  TURNKEY: 2,
} as const;

export interface CollaboratorOrgBrief {
  orgId: string;
  orgCode?: string | null;
  name: string;
  orgType: number;
}

export interface CollaboratorOrg {
  id: string;
  projectId: string;
  collabOrgId: string;
  role: CollabRole;
  contractType: CollaboratorContractType;
  allowedFunctionalRoles: FunctionalRoleCode[];
  addedRoleCodes?: FunctionalRoleCode[];
  addedRoleLabels?: string[];
  removedRoleCodes?: FunctionalRoleCode[];
  removedRoleLabels?: string[];
  affectedMemberUserIds?: string[];
  leftWithoutFunctionalRoleUserIds?: string[];
  status: CollabStatus;
  invitedBy: string;
  respondedBy?: string | null;
  respondedAt?: string | null;
  /** 邀请失效时间（UTC ISO）；仅 pending 有意义，过期后自动置为 expired */
  expiresAt?: string | null;
  /** @deprecated 兼容历史数据；前端新逻辑不再读取或写入该字段。 */
  assignedProducerUserId?: string | null;
  assignedBy?: string | null;
  assignedAt?: string | null;
  createdAt: string;
  updatedAt: string;
  org?: CollaboratorOrgBrief | null;
  canDownload?: boolean;
  canManageAllAssets?: boolean;
  canManageMembers?: boolean;
}

export interface CollaboratorOrgListResponse {
  items: CollaboratorOrg[];
  total: number;
}

export interface InviteCollaboratorOrgDto {
  orgId?: string;
  orgCode?: string;
  role?: CollabRole;
  contractType: CollaboratorContractType;
  allowedFunctionalRoles: FunctionalRoleCode[];
  canDownload?: boolean;
  canManageMembers?: boolean;
  canCreateScreenplays?: boolean;
  canManageAllAssets?: boolean;
}

export interface BatchInviteCollaboratorOrgDto extends InviteCollaboratorOrgDto {
  episodeIds: string[];
}

export interface BatchInviteCollaboratorOrgResult {
  createdCount: number;
  skippedCount: number;
  failureCount: number;
}

export interface ProjectEntryContextOption {
  kind: 'host' | 'contractor';
  orgId: string;
  orgName: string;
  orgType: number;
  pcoId?: string | null;
  label: string;
}

export interface ProjectEntryContext {
  projectId: string;
  activeOrgId?: string | null;
  requiresIdentitySelection: boolean;
  defaultOrgId?: string | null;
  options: ProjectEntryContextOption[];
}

export interface UpdateCollaboratorOrgAllowedFunctionalRolesDto {
  allowedFunctionalRoles: FunctionalRoleCode[];
  canCreateScreenplays?: boolean;
  canManageAllAssets?: boolean;
}

export interface AddCollaboratorOrgMemberDto {
  userId: string;
  role?: CollabRole;
  expiresAt?: string | null;
  groupIds?: string[];
  functionalRoles?: FunctionalRoleCode[];
  canManageMembers?: boolean;
  canCreateScreenplays?: boolean;
  canDownload?: boolean;
}

export interface UpdateCollaboratorOrgMemberRolesDto {
  functionalRoles: FunctionalRoleCode[];
  canManageMembers?: boolean;
  canCreateScreenplays?: boolean;
  canDownload?: boolean;
}

export interface CollaboratorOrgMemberCandidate {
  pcoId: string;
  collabOrgName?: string | null;
  userId: string;
  username: string;
  realName?: string | null;
  nickname?: string | null;
  email?: string | null;
  phone?: string | null;
  avatarUrl?: string | null;
  memberType: 'regular' | 'temp';
  orgRole: string;
  roleCeiling: CollabRole;
  alreadyInProject: boolean;
  excludedReason?: string | null;
}

export interface CollaboratorOrgMemberCandidateListResponse {
  items: CollaboratorOrgMemberCandidate[];
  total: number;
}

export interface PendingCollaborationItem {
  id: string;
  project: { id: string; name: string; thumbnailUrl?: string | null };
  inviterOrg?: CollaboratorOrgBrief | null;
  role: CollabRole;
  contractType: CollaboratorContractType;
  allowedFunctionalRoles: FunctionalRoleCode[];
  status: CollabStatus;
  invitedBy: string;
  respondedBy?: string | null;
  /** 邀请失效时间（UTC ISO）；超过后自动失效，邀请方可重新发起 */
  expiresAt?: string | null;
  /** @deprecated 兼容历史数据；前端新逻辑不再读取或写入该字段。 */
  assignedProducerUserId?: string | null;
  createdAt: string;
}

export interface PendingCollaborationListResponse {
  items: PendingCollaborationItem[];
  total: number;
}

/**
 * 项目列表响应
 */
export interface InProjectDirectoryFolder {
  id: string;
  name: string;
  parentFolderId?: string | null;
  color?: string | null;
  description?: string | null;
  sortOrder?: number | null;
  createdAt?: string | null;
  subprojectCount: number;
}

export interface ParentDirectoryResponse {
  folders: InProjectDirectoryFolder[];
  rootSubprojectCount: number;
}

export type DirectorySubprojectSort =
  | 'display_order_desc'
  | 'display_order_asc'
  | 'created_at_desc'
  | 'created_at_asc';

export interface DirectorySubproject {
  id: string;
  name: string;
  screenplayId?: string | null;
  folderId?: string | null;
  displayOrder: number;
  createdAt?: string | null;
  aspectRatio?: string | null;
  styleId?: string | null;
  visualStyle?: string | null;
  parentProjectId?: string | null;
  orgId?: string | null;
  ownerId?: string | null;
  status?: Project['status'] | null;
  memberRole?: Project['memberRole'];
  functionalRoles?: Project['functionalRoles'];
}

export interface DirectorySubprojectsResponse {
  items: DirectorySubproject[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ProjectListResponse {
  items: Project[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  folders?: InProjectDirectoryFolder[];
}

/**
 * 回收站项目
 */
export interface TrashProject {
  id: string;
  name: string;
  description?: string;
  type: ProjectType;
  folderId?: string;
  thumbnailUrl?: string;
  status: typeof PROJECT_STATUS.TRASHED;
  trashedAt: string;
  daysRemaining: number; // 剩余天数
  createdAt: string;
  updatedAt: string;
}

/**
 * 回收站列表响应
 */
export interface TrashListResponse {
  items: TrashProject[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * 恢复项目响应
 */
export interface RestoreProjectResponse {
  id: string;
  name: string;
  status: ProjectStatus;
  message: string;
}

export interface CreateProjectExportResponse {
  exportJobId: string;
  status: string;
  progress: number;
  wsTicket?: string | null;
}

export interface ProjectExportTypeSelection {
  includeVideos?: boolean;
  includeDialogues?: boolean;
  includeVoiceovers?: boolean;
  includeSoundEffects?: boolean;
  includeAudios?: boolean;
  includeStoryboardImages?: boolean;
  includeStoryboardImageHistory?: boolean;
  includeStoryboardUnfinalizedImages?: boolean;
}

export interface ProjectExportProjectSelection {
  projectId: string;
}

export interface ProjectExportResourceLibrarySelection {
  includeCharacters?: boolean;
  includeLocations?: boolean;
  includeProps?: boolean;
  includeFootage?: boolean;
  includeHistory?: boolean;
}

export interface ProjectExportCapabilitiesResponse {
  scope: 'personal' | 'team' | 'enterprise';
  canExportAssets: boolean;
}

export interface ProjectExportRequest {
  projects: ProjectExportProjectSelection[];
  exportTypes?: ProjectExportTypeSelection;
  resourceLibrary?: ProjectExportResourceLibrarySelection;
}

export interface ProjectExportStatusResponse {
  exportJobId?: string;
  status: string;
  stage?: string | null;
  progress: number;
  fileName?: string | null;
  fileSize?: number | null;
  expiresAt?: string | null;
  canDownload: boolean;
  errorMessage?: string | null;
  creditsUsed?: number | null;
  sourceSizeBytes?: number | null;
}

export interface ProjectExportDownloadUrlResponse {
  /** 公开域直链，无签名 query；expiresIn 为导出包剩余可下载秒数 */
  url: string;
  expiresIn: number;
  fileName: string;
}

export interface ProjectExportWsEvent {
  type: string;
  jobId: string;
  status?: string | null;
  stage?: string | null;
  progress?: number | null;
  message?: string | null;
  canDownload?: boolean | null;
  fileName?: string | null;
  fileSize?: number | null;
  expiresAt?: string | null;
  errorMessage?: string | null;
  creditsUsed?: number | null;
  sourceSizeBytes?: number | null;
}

export type SharePermission = 'viewer' | 'editor';

export interface SharedUserSummary {
  userId: string;
  username?: string | null;
  nickname?: string | null;
  email?: string | null;
  avatarUrl?: string | null;
}

export interface ProjectShare {
  shareId: string;
  shareUrl: string;
  permission: SharePermission;
  createdBy: string;
  createdAt: string;
  expiresAt?: string | null;
  hasPassword: boolean;
  accessCount: number;
  maxAccessCount?: number | null;
  remainingAccessCount?: number | null;
  status?: 'active' | 'inactive';
  invalidReason?: string | null;
  contentScope?: string | null;
  revokedAt?: string | null;
}

export interface ProjectShareListResponse {
  shares: ProjectShare[];
}

/**
 * RFC-003 §4.3：项目成员上挂载的权限组引用
 */
export interface ProjectPermissionGroupAssignment {
  groupId: string;
  name: string;
  isSystem: boolean;
}

/**
 * RFC-003 §6.2：项目可用的业务权限组（GET /projects/{id}/permission-groups）
 */
export interface ProjectPermissionGroupOption {
  groupId: string;
  name: string;
  description?: string | null;
  isSystem: boolean;
  /** 与 PROJECT_SCOPED 白名单的交集权限码 */
  permissionCodes: string[];
}

export interface ProjectMemberFunctionalRole {
  code: FunctionalRoleCode;
  label: string;
  canManageMembers: boolean;
  canCreateScreenplays?: boolean;
}

/**
 * RFC-003 §6.1：可用于项目邀请的组织成员
 */
export interface AvailableOrgMember {
  userId: string;
  username?: string | null;
  realName?: string | null;
  email?: string | null;
  phone?: string | null;
  nickname?: string | null;
  avatarUrl?: string | null;
  orgRole: 'owner' | 'admin' | 'member';
  roleTemplateCode?: RoleTemplateCode | null;
  positionIds?: string[];
  memberSource?: 'internal';
  /** 是否已经是该项目成员 */
  alreadyInProject: boolean;
  /** 不可邀请原因；存在时前端禁用该候选 */
  excludedReason?: string | null;
}

export interface ProjectMember {
  id: string;
  projectId: string;
  userId: string;
  role: SharePermission | 'owner';
  realName?: string | null;
  invitedBy?: string | null;
  grantedByShareId?: string | null;
  grantedByPcoId?: string | null;
  joinedAt: string;
  createdAt: string;
  username?: string | null;
  nickname?: string | null;
  email?: string | null;
  phone?: string | null;
  avatarUrl?: string | null;
  orgRole?: OrgMemberRole | null;
  roleTemplateCode?: RoleTemplateCode | null;
  /** RFC-003 §4.3：该成员挂载到的权限组列表 */
  permissionGroups?: ProjectPermissionGroupAssignment[];
  functionalRoles?: ProjectMemberFunctionalRole[];
  permissionRestrictionReason?: string | null;
  disabledReason?: string | null;
  memberSource?: 'internal' | 'pco';
  pcoOrgName?: string | null;
  memberType?: 'regular' | 'temp' | null;
  canDownload?: boolean;
  canManageMembers?: boolean;
  /** 父项目建集权。单集成员列表与父项目状态一致 */
  canCreateScreenplays?: boolean;
}

export interface AddProjectMemberDto {
  userId: string;
  role: SharePermission | 'owner';
  groupIds?: string[] | null;
  functionalRoles?: FunctionalRoleCode[] | null;
  canManageMembers?: boolean;
  canCreateScreenplays?: boolean;
  canDownload?: boolean;
}

export interface UpdateProjectMemberDto {
  role?: SharePermission | 'owner';
  groupIds?: string[] | null;
  functionalRoles?: FunctionalRoleCode[] | null;
  canManageMembers?: boolean | null;
  canCreateScreenplays?: boolean | null;
  canDownload?: boolean | null;
}

export interface ReplaceProjectProducerDto {
  producerUserId: string;
}

export interface ProjectProducerReplaceResponse {
  rootProjectId: string;
  entryProjectId: string;
  previousProducerUserId: string;
  producerUserId: string;
  affectedProjectCount: number;
  transferredTokenAmount: number;
  transferredAllocationCount: number;
}

export interface ProjectMemberListResponse {
  items: ProjectMember[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface ProducerSetupMember {
  userId: string;
  username?: string | null;
  realName?: string | null;
  nickname?: string | null;
  email?: string | null;
  phone?: string | null;
  avatarUrl?: string | null;
  orgRole?: number | null;
  roleTemplateCode?: RoleTemplateCode | string | null;
  memberType?: 1 | 2 | null;
  alreadyInProject: boolean;
  alreadyDirector: boolean;
  disabledReason?: string | null;
  canManageMembers?: boolean;
  canCreateScreenplays?: boolean;
  canDownload?: boolean;
  functionalRoles?: ProjectMemberFunctionalRole[];
}

export interface ProducerSetupMemberAssignment {
  userId: string;
  functionalRoles: FunctionalRoleCode[];
  canManageMembers?: boolean;
  canCreateScreenplays?: boolean;
  canDownload?: boolean;
}

export type MemberConfigInheritanceSourceType = 'root_bootstrap' | 'subproject' | 'none';

export interface MemberConfigInheritanceRequest {
  enabled: boolean;
  sourceType: MemberConfigInheritanceSourceType;
  sourceProjectId?: string | null;
}

export interface MemberConfigInheritanceSummary extends MemberConfigInheritanceRequest {
  sourceProjectName?: string | null;
}

export type CreateSubprojectMemberSource = {
  /** 本方最终花名册；组织项目建集必填 */
  memberAssignments?: BatchSubprojectMemberAssignment[];
  /** 对侧团队：从 sourceSubprojectId 整团拷贝成员与职能 */
  copyOrgIds?: string[];
  /** 对侧拷贝来源子项目；有 copyOrgIds 时必填 */
  sourceSubprojectId?: string;
};

export interface BatchSubprojectMemberAssignment {
  userId: string;
  functionalRoles: FunctionalRoleCode[];
  canManageMembers?: boolean;
  canCreateScreenplays?: boolean;
  parentCanCreateScreenplays?: boolean | null;
  canDownload?: boolean;
}

export interface BatchSubprojectOrgAssignment {
  orgId: string;
  allowedFunctionalRoles: FunctionalRoleCode[];
  canDownload?: boolean;
  canCreateScreenplays?: boolean;
  canManageAllAssets?: boolean;
}

export interface BatchAddSubprojectMembersRequest {
  allSubprojects: boolean;
  targetProjectIds: string[];
  memberAssignments: BatchSubprojectMemberAssignment[];
  orgAssignments?: BatchSubprojectOrgAssignment[];
}

export interface BatchSubprojectMemberResult {
  projectId: string;
  projectName: string;
  success: boolean;
  addedCount: number;
  mergedCount: number;
  skippedCount: number;
  orgUpdatedCount?: number;
  orgCreatedCount?: number;
  errorCode?: string | null;
  message?: string | null;
}

export interface BatchAddSubprojectMembersResponse {
  results: BatchSubprojectMemberResult[];
  successCount: number;
  failureCount: number;
}

export interface ParentProjectMemberManagementSubproject {
  projectId: string;
  projectName: string;
  status: ProjectStatus;
  writable: boolean;
  disabledReason?: string | null;
  allowedFunctionalRoles?: FunctionalRoleCode[];
  canConfigureDirector: boolean;
  canGrantMemberManagement?: boolean;
  canConfigureDirectorScreenplays?: boolean;
  canDownload?: boolean;
}

export interface ParentProjectMemberManagementMember {
  userId: string;
  username?: string | null;
  realName?: string | null;
  nickname?: string | null;
  email?: string | null;
  phone?: string | null;
  avatarUrl?: string | null;
  orgRole?: OrgMemberRole | number | null;
  roleTemplateCode?: RoleTemplateCode | string | null;
  memberSource: 'internal' | 'pco';
  pcoOrgName?: string | null;
  removable: boolean;
  disabledReason?: string | null;
  functionalRoles: ProjectMemberFunctionalRole[];
  canCreateScreenplays?: boolean;
}

export interface ParentProjectMemberManagementAssignment {
  projectId: string;
  projectName: string;
  userId: string;
  memberSource: 'internal' | 'pco';
  removable: boolean;
  disabledReason?: string | null;
  allowedFunctionalRoles?: FunctionalRoleCode[];
  functionalRoles: ProjectMemberFunctionalRole[];
  canDownload?: boolean;
  canManageMembers?: boolean;
  canCreateScreenplays?: boolean;
}

export interface ParentProjectMemberManagementCollaboratorEpisode {
  projectId: string;
  pcoId: string;
  contractType: number;
  canDownload: boolean;
  allowedFunctionalRoles: string[];
  status: number;
  expiresAt?: string | null;
}

export interface ParentProjectMemberManagementCollaborator {
  orgId: string;
  orgName: string;
  hasScreenplayGrant: boolean;
  canManageAllAssets?: boolean;
  episodes: ParentProjectMemberManagementCollaboratorEpisode[];
}

export interface ParentProjectMemberManagementContext {
  parentProjectId: string;
  parentProjectName: string;
  subprojects: ParentProjectMemberManagementSubproject[];
  members: ParentProjectMemberManagementMember[];
  assignments: ParentProjectMemberManagementAssignment[];
  collaborators?: ParentProjectMemberManagementCollaborator[];
  roles: FunctionalRoleDefinition[];
}

export interface BatchRemoveSubprojectMemberItem {
  projectId: string;
  userId: string;
}

export interface BatchRemoveSubprojectMembersRequest {
  items: BatchRemoveSubprojectMemberItem[];
}

export interface BatchRemoveSubprojectMemberResult {
  projectId: string;
  projectName: string;
  userId: string;
  success: boolean;
  removedCount: number;
  errorCode?: string | null;
  message?: string | null;
}

export interface BatchRemoveSubprojectMembersResponse {
  results: BatchRemoveSubprojectMemberResult[];
  successCount: number;
  failureCount: number;
}

export interface BatchUpdateSubprojectMemberRoleItem {
  projectId: string;
  userId: string;
  functionalRoles: FunctionalRoleCode[];
  canManageMembers?: boolean;
  canCreateScreenplays?: boolean;
  parentCanCreateScreenplays?: boolean | null;
  canDownload?: boolean;
}

export interface BatchUpdateSubprojectMemberRolesRequest {
  items: BatchUpdateSubprojectMemberRoleItem[];
}

export interface BatchUpdateSubprojectMemberRoleResult {
  projectId: string;
  projectName: string;
  userId: string;
  success: boolean;
  updatedCount: number;
  errorCode?: string | null;
  message?: string | null;
}

export interface BatchUpdateSubprojectMemberRolesResponse {
  results: BatchUpdateSubprojectMemberRoleResult[];
  successCount: number;
  failureCount: number;
}

export interface SubprojectMemberTarget {
  projectId: string;
  projectName: string;
  status: ProjectStatus;
  writable: boolean;
  disabledReason?: string | null;
}

export interface SubprojectMemberTargetListResponse {
  items: SubprojectMemberTarget[];
}

export interface ProducerSetupRequest {
  directorUserIds: string[];
  directorCanManageMembers: boolean;
  directorCanCreateScreenplays?: boolean;
  memberAssignments?: ProducerSetupMemberAssignment[];
  memberConfigInheritance?: MemberConfigInheritanceRequest;
  /** @deprecated 项目筹建必须配置至少一名导演，不再允许稍后设置。 */
  skipSetup?: boolean;
  note?: string | null;
}

export interface ProducerSetupResponse {
  projectId: string;
  projectName: string;
  orgType?: number | null;
  setupMode?: 'enterprise' | 'team' | string | null;
  completed: boolean;
  canEdit: boolean;
  readonlyReason?: string | null;
  blockingReasons: string[];
  nextAction?: 'producerSetup' | 'directorSetup' | 'memberSetup' | 'parseFlow' | string | null;
  directorCanManageMembers: boolean;
  directorCanCreateScreenplays?: boolean;
  producerSetupCompletedAt?: string | null;
  memberSetupCompletedAt?: string | null;
  note?: string | null;
  memberConfigInheritance?: MemberConfigInheritanceRequest | null;
  hasSubprojects?: boolean;
  memberScopeLevel?: 1 | 2 | number;
  configuredSubprojectCount?: number;
  skippedSubprojectCount?: number;
  affectedProjectIds?: string[];
  directors: ProducerSetupMember[];
  members: ProducerSetupMember[];
  candidates: ProducerSetupMember[];
}

export interface DirectorBaseSetupRequest {
  aspectRatio: AspectRatioType;
  styleId?: string | null;
  visualStyle?: string | null;
  note?: string | null;
}

export interface DirectorBaseSetupResponse {
  projectId: string;
  projectName: string;
  completed: boolean;
  canEdit: boolean;
  readonlyReason?: string | null;
  blockingReasons: string[];
  nextAction?: 'producerSetup' | 'directorSetup' | 'parseFlow' | string | null;
  aspectRatio?: AspectRatioType | null;
  styleId?: string | null;
  visualStyle?: string | null;
  note?: string | null;
  enterpriseBaseSetupCompletedAt?: string | null;
}

export interface MemberSetupCompleteRequest {
  completionMode: 'configured' | 'skipped';
  note?: string | null;
}

export interface MemberSetupCompleteResponse {
  projectId: string;
  projectName: string;
  memberSetupCompletedAt: string;
  memberSetupCompletedBy: string;
  memberSetupCompletionMode: 'configured' | 'skipped';
  note?: string | null;
}

export interface PublicProjectShareInfo {
  project: Pick<Project, 'id' | 'name' | 'description' | 'thumbnailUrl'>;
  share: {
    permission: SharePermission;
    hasPassword: boolean;
    expiresAt?: string | null;
    isExpired?: boolean;
    accessCount: number;
    maxAccessCount?: number | null;
    remainingAccessCount?: number | null;
    status?: 'active' | 'inactive';
    invalidReason?: string | null;
    contentScope?: string | null;
  };
}

export interface PublicProjectShareAccess {
  project: Project;
  share: {
    permission: SharePermission;
    expiresAt?: string | null;
    hasPassword: boolean;
    accessCount: number;
    maxAccessCount?: number | null;
    remainingAccessCount?: number | null;
    contentScope?: string | null;
  };
  member?: {
    userId: string;
    role: SharePermission;
    grantedByShareId?: string | null;
  };
  claimStatus: 'readonly';
}

export interface TransferProjectToOrgDto {
  targetOrgId: string;
  targetOrgType: 1 | 2;
  aiCreditsBudget?: number;
  producerUserId?: string | null;
  adminParticipatesInProject?: boolean;
}

export interface TransferProjectToOrgResponse {
  projectId: string;
  orgId: string;
  orgType: 1 | 2;
  privateTokenConsumed: number;
  privateTokenRefunded: number;
  privateNetTokenConsumed: number;
  migrationSummaryId?: string | null;
  budgetApplied: boolean;
  appliedEpisodeCreditsBudget: number;
  appliedTotalCreditsBudget: number;
  appliedEpisodeCount: number;
  budgetAllocationPending: boolean;
}
